import * as React from 'react';

import {useDroppable} from '@dnd-kit/react';

import {classify} from 'src/utils/classify';

import {DropIndicator} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/drop-indicator';
import type {IndicatorTarget} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/hooks/use-drag-indicator';
import layoutCss from './dashboard-builder-canvas-layout.css';


export type DroppableRowDividerProps = {
  afterIndex: number;
  variant: 'kpi' | 'viz';
  indicatorTarget: IndicatorTarget | null;
  activeDragType: 'kpi-tiles' | 'viz-tiles' | null;
  label: string;
  children?: React.ReactNode;
};

/**
 * Inter-row drop zone. Rendering a droppable here lets the user drag a tile
 * onto an empty band between rows and create a brand-new row of the matching
 * type. `afterIndex` of -1 prepends as the first row.
 */
export function DroppableRowDivider({
  afterIndex,
  variant,
  indicatorTarget,
  activeDragType,
  label,
  children,
}: DroppableRowDividerProps) {
  const dropType = variant === 'kpi' ? 'kpi-tiles' : 'viz-tiles';
  const droppableId = `new-row-${variant}-after-${afterIndex}`;
  const isCompatibleWithDrag =
    activeDragType !== null && activeDragType === dropType;

  const {ref, isDropTarget} = useDroppable({
    id: droppableId,
    accept: dropType,
    data: {type: dropType, variant, afterIndex},
    disabled: activeDragType !== null && activeDragType !== dropType,
  });

  const isIndicatorTarget =
    indicatorTarget !== null &&
    indicatorTarget.droppableId === droppableId &&
    indicatorTarget.orientation === 'horizontal';

  return (
    <div
      ref={ref}
      data-byod-droppable-id={droppableId}
      className={classify(
        layoutCss.droppableRowDivider,
        isCompatibleWithDrag && layoutCss.droppableRowDividerCompatible,
        isDropTarget && layoutCss.droppableRowDividerHovered,
      )}
    >
      {children}
      {isIndicatorTarget && (
        <DropIndicator orientation="horizontal" offset={0} />
      )}
      {isDropTarget && (
        <span className={layoutCss.droppableRowDividerLabel} aria-hidden>
          {label}
        </span>
      )}
    </div>
  );
}
